import '@fontsource/barlow-condensed/600.css';
import { useRef, type CSSProperties } from 'react';
import { splitPlate } from '../domain/passes';
import { maskPlate, maskRegion, normalizePlate, PLATE_TEMPLATE } from '../domain/plate';
import s from './gostPlate.module.css';

/**
 * Russian plate drawn after GOST R 50577 (type 1): 520×112 mm, digits 58 mm high, letters 42 mm, region on the right
 * behind a vertical line with «RUS» and the flag under it. The face is Barlow Condensed (SIL OFL); it has no
 * Cyrillic, but the 12 plate letters are all Latin look-alikes, so they are drawn with their Latin twins.
 * Only a plate that fits the template «Б 999 ББ» + 2–3 region digits is drawn; anything else (old data, foreign
 * plates) stays a plain line, so it is never forced into the wrong places.
 */
const LATIN: Record<string, string> = { А: 'A', В: 'B', Е: 'E', К: 'K', М: 'M', Н: 'H', О: 'O', Р: 'P', С: 'C', Т: 'T', У: 'Y', Х: 'X' };
const TEMPLATE = 'A000AA';
const PRIVATE = /^([АВЕКМНОРСТУХ]\d{3}[АВЕКМНОРСТУХ]{2})(\d{2,3})$/u;

export interface Glyph {
  ch: string;
  kind: 'letter' | 'digit';
  /** false: a grey template character the resident has not typed yet. */
  filled: boolean;
}

const latin = (ch: string) => LATIN[ch] ?? ch;

/**
 * The characters of a plate with their size class, or null when it does not fit the template; with `template`,
 * the main part being typed over «А000АА» (the input has already been masked).
 */
export function plateGlyphs(value: string, { template = false } = {}): { glyphs: Glyph[]; region: string } | null {
  if (template) {
    return {
      glyphs: PLATE_TEMPLATE.map((kind, i) => (i < value.length ? { ch: latin(value[i]), kind, filled: true } : { ch: TEMPLATE[i], kind, filled: false })),
      region: '',
    };
  }
  // The server keeps plates in its own form, often with Latin letters («T001TT77»): read them as Cyrillic first.
  const plate = normalizePlate(value);
  const m = PRIVATE.exec(plate);
  if (!m) return null;
  return { glyphs: [...m[1]].map((ch, i) => ({ ch: latin(ch), kind: PLATE_TEMPLATE[i], filled: true })), region: m[2] };
}

function Main({ glyphs, caret }: { glyphs: Glyph[]; caret?: number }) {
  return (
    <span className={s.main} aria-hidden="true">
      {glyphs.map((g, i) => (
        <span key={i} className={`${s[g.kind]} ${g.filled ? '' : s.empty} ${i === 0 || i === 3 ? s.gap : ''}`}>
          {caret === i && <i className={s.caret} />}
          {g.ch}
        </span>
      ))}
      {caret === glyphs.length && <i className={s.caret} />}
    </span>
  );
}

function Region({ value, placeholder, caret }: { value: string; placeholder?: string; caret?: boolean }) {
  return (
    <span className={s.region} aria-hidden="true">
      <span className={`${s.regionNum} ${value ? '' : s.empty}`}>
        {value || placeholder}
        {caret && <i className={s.caret} />}
      </span>
      <span className={s.rus}>
        RUS<span className={s.flag} />
      </span>
    </span>
  );
}

/** A plate in a list or a receipt. `height` in px; the width follows the 520:112 proportion. */
export function GostPlate({ value, height = 24 }: { value: string; height?: number }) {
  const laid = plateGlyphs(value);
  if (!laid) return <span className={s.raw}>{value}</span>;
  const { number, region: spoken } = splitPlate(value);
  return (
    <span className={s.plate} style={{ '--h': `${height}px` } as CSSProperties} aria-label={`Госномер ${number} ${spoken}`}>
      <Main glyphs={laid.glyphs} />
      <Region value={laid.region} />
    </span>
  );
}

interface InputProps {
  plate: string;
  region: string;
  focused: 'plate' | 'region' | null;
  onPlate(value: string): void;
  onRegion(value: string): void;
  onFocus(field: 'plate' | 'region'): void;
  onBlur(): void;
  height?: number;
  /** A short hint inside the empty plate, under the template (instead of a line of its own under the field). */
  note?: string;
}

/**
 * The plate as the input on the home screen: what is typed appears on a real-looking plate. The inputs lie over the
 * drawing, transparent; the drawing shows its own caret because letters and digits differ in size. The mask lets
 * only a plate letter onto a letter place and only a digit onto a digit place; once the six places are full the
 * caret moves on to the region, and backspace in an empty region comes back to the number.
 */
export function GostPlateInput({ plate, region, focused, onPlate, onRegion, onFocus, onBlur, height = 70, note }: InputProps) {
  const { glyphs } = plateGlyphs(plate, { template: true })!;
  const mainRef = useRef<HTMLInputElement>(null);
  const regionRef = useRef<HTMLInputElement>(null);
  const typePlate = (value: string) => {
    const masked = maskPlate(value);
    onPlate(masked.plate);
    if (masked.region) onRegion(maskRegion(region + masked.region));
    if (masked.plate.length === PLATE_TEMPLATE.length && plate.length < PLATE_TEMPLATE.length) regionRef.current?.focus();
  };
  return (
    <span className={`${s.plate} ${s.input}`} style={{ '--h': `${height}px` } as CSSProperties}>
      <Main glyphs={glyphs} caret={focused === 'plate' ? Math.min(plate.length, glyphs.length) : undefined} />
      {note && !plate && <span className={s.note} aria-hidden="true">{note}</span>}
      <Region value={region} placeholder="77" caret={focused === 'region'} />
      <input
        ref={mainRef}
        className={s.mainInput}
        aria-label="Госномер гостя"
        value={plate}
        autoComplete="off"
        autoCapitalize="characters"
        onFocus={() => onFocus('plate')}
        onBlur={onBlur}
        onChange={(e) => typePlate(e.target.value)}
      />
      <input
        ref={regionRef}
        className={s.regionInput}
        aria-label="Регион"
        value={region}
        inputMode="numeric"
        autoComplete="off"
        onFocus={() => onFocus('region')}
        onBlur={onBlur}
        onChange={(e) => onRegion(maskRegion(e.target.value))}
        onKeyDown={(e) => {
          if (e.key !== 'Backspace' || region) return;
          e.preventDefault();
          mainRef.current?.focus();
          onPlate(plate.slice(0, -1));
        }}
      />
    </span>
  );
}
